/**
 * Onboarding Layout - Step indicator + content shell
 */

import { ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import { Rocket24Regular } from '@fluentui/react-icons';
import Header from '../landing/Header';
import Footer from '../landing/Footer';
import PageHero from '../layout/PageHero';

interface OnboardingLayoutProps {
  children: ReactNode;
  currentStep:
    | 'loading'
    | 'TenantSetup'
    | 'Welcome'
    | 'ConfirmOrg'
    | 'ConnectSharePoint'
    | 'RunScan'
    | 'Complete';
}

const steps = [
  { id: 'TenantSetup', name: 'Tenant Setup' },
  { id: 'Welcome', name: 'Welcome' },
  { id: 'ConfirmOrg', name: 'Confirm Org' },
  { id: 'ConnectSharePoint', name: 'Add Sites' },
  { id: 'RunScan', name: 'Run Scan' },
];

export default function OnboardingLayout({
  children,
  currentStep,
}: OnboardingLayoutProps) {
  const { instance } = useMsal();
  const currentIndex = steps.findIndex((s) => s.id === currentStep);

  return (
    <div className='min-h-screen flex flex-col bg-gray-50'>
      <Header isAuthenticated={true} onSignOut={() => instance.logoutPopup()} />

      <PageHero
        icon={<Rocket24Regular style={{ width: 22, height: 22 }} />}
        title='Workspace Setup'
        subtitle='Connect SharePoint and run your first scan to get it ready for your tenant.'
      />

      {/* Progress Steps — hidden on loading/complete screens */}
      {currentStep !== 'Complete' && currentStep !== 'loading' && (
        <nav
          aria-label='Progress'
          className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full'
        >
          <ol role='list' className='flex items-center'>
            {steps.map((step, index) => (
              <li
                key={step.id}
                className={`relative ${index !== steps.length - 1 ? 'pr-8 sm:pr-20 flex-1' : ''}`}
              >
                {/* Connector line between steps */}
                {index !== steps.length - 1 && (
                  <div
                    className='absolute inset-0 flex items-center'
                    aria-hidden='true'
                  >
                    <div
                      className={`h-0.5 w-full ${index < currentIndex ? 'bg-[#2ba6bb]' : 'bg-gray-200'}`}
                    />
                  </div>
                )}
                {/* Step circle */}
                <span
                  className={`relative flex h-8 w-8 items-center justify-center rounded-full ${
                    index < currentIndex
                      ? 'bg-[#2ba6bb]'
                      : index === currentIndex
                        ? 'border-2 border-[#2ba6bb] bg-[#2ba6bb]/10 ring-4 ring-[#2ba6bb]/20'
                        : 'border-2 border-gray-300 bg-white'
                  }`}
                >
                  {index < currentIndex ? (
                    <svg
                      className='h-5 w-5 text-white'
                      viewBox='0 0 20 20'
                      fill='currentColor'
                      aria-hidden='true'
                    >
                      <path
                        fillRule='evenodd'
                        d='M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z'
                        clipRule='evenodd'
                      />
                    </svg>
                  ) : index === currentIndex ? (
                    <span className='h-3 w-3 rounded-full bg-[#2ba6bb]' />
                  ) : (
                    <span className='h-2.5 w-2.5 rounded-full bg-transparent' />
                  )}
                </span>
                <span
                  className={`absolute top-10 left-1/2 transform -translate-x-1/2 whitespace-nowrap text-xs ${
                    index <= currentIndex
                      ? 'text-[#228a9d] font-medium'
                      : 'text-gray-500'
                  }`}
                >
                  {step.name}
                </span>
              </li>
            ))}
          </ol>
        </nav>
      )}

      {/* Main Content */}
      <main className='flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 w-full'>
        {children}
      </main>

      <Footer />
    </div>
  );
}
